import React, { useEffect, useState } from "react";
import { api, formatCHF, currentMonthString, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  datum: new Date().toISOString().slice(0, 10),
  kategorie: "Treibstoff",
  beschreibung: "",
  betrag: 0,
  fahrzeug_id: "",
  bemerkung: "",
};

const KATEGORIEN = ["Treibstoff", "Reparatur", "Versicherung", "Maut", "Reifen", "Wartung", "Sonstiges"];

export default function Expenses() {
  const [monat, setMonat] = useState(currentMonthString());
  const [list, setList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [r, v] = await Promise.all([
      api.get("/expenses", { params: { monat } }),
      api.get("/vehicles"),
    ]);
    setList(r.data); setVehicles(v.data);
  };
  useEffect(() => { load(); }, [monat]);

  const vehName = (id) => vehicles.find((v) => v.id === id)?.kennzeichen || "—";

  const save = async () => {
    try {
      const payload = {
        ...form,
        betrag: Number(form.betrag) || 0,
        fahrzeug_id: form.fahrzeug_id || null,
      };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success("Gespeichert");
      setOpen(false); load();
    } catch { toast.error("Fehler"); }
  };

  const total = list.reduce((s, e) => s + (e.betrag || 0), 0);

  return (
    <PageContainer>
      <PageHeader title="Sonstige Kosten" subtitle="Treibstoff, Reparatur, Maut etc." testId="expenses-page">
        <MonthPicker value={monat} onChange={setMonat} testId="ex-month-picker" />
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="add-expense-button">
          <Plus className="h-4 w-4 mr-2" /> Neuer Eintrag
        </Button>
      </PageHeader>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead><TableHead>Kategorie</TableHead><TableHead>Beschreibung</TableHead>
                <TableHead>Fahrzeug</TableHead><TableHead className="text-right">Betrag</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">Keine Kosten erfasst.</TableCell></TableRow>)}
              {list.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="font-mono">{formatDatum(e.datum)}</TableCell>
                  <TableCell>{e.kategorie}</TableCell>
                  <TableCell>{e.beschreibung}</TableCell>
                  <TableCell className="font-mono">{vehName(e.fahrzeug_id)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(e.betrag)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(e); setForm({ ...EMPTY, ...e, fahrzeug_id: e.fahrzeug_id || "" }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={async () => { if(window.confirm("Löschen?")){ await api.delete(`/expenses/${e.id}`); load();}}}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {list.length > 0 && (
                <TableRow className="bg-neutral-50">
                  <TableCell colSpan={4} className="text-right font-semibold">Total</TableCell>
                  <TableCell className="nivara-num font-semibold">{formatCHF(total)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Kosten bearbeiten" : "Neue Kosten"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Datum</Label><Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select value={form.kategorie} onValueChange={(v) => setForm({...form, kategorie: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{KATEGORIEN.map(k => (<SelectItem key={k} value={k}>{k}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2"><Label>Beschreibung</Label><Input value={form.beschreibung} onChange={(e) => setForm({...form, beschreibung: e.target.value})} /></div>
            <div className="space-y-2"><Label>Betrag (CHF)</Label><Input type="number" step="0.01" value={form.betrag} onChange={(e) => setForm({...form, betrag: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2">
              <Label>Fahrzeug (optional)</Label>
              <Select value={form.fahrzeug_id || "_none"} onValueChange={(v) => setForm({...form, fahrzeug_id: v === "_none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">— Kein Fahrzeug —</SelectItem>{vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.kennzeichen}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2"><Label>Bemerkung</Label><Input value={form.bemerkung || ""} onChange={(e) => setForm({...form, bemerkung: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
